import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { HttpErrorResponse } from "@angular/common/http";
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { filter, mergeMap } from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';
import { MessageDialogComponent } from '../../../../shared/dialogs/components/message-dialog.component';

import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';

import { AccesService } from '../services/acces.service';
import { AccesTLR } from '../entities/acces-tlr.entity';
import { AssociationArretsOrder } from '../entities/association-arrets-order.entity';
import { OuvrageAccesDTO } from '../entities/ouvrage-acces-dto.entity';

import { ArretSlbTLR } from '../../arret-slb/entities/arret-slb-tlr.entity';

// Fonction qui contourne le probleme que les forkJoin de [] ne se resolvent jamais
let safeForkJoin = <T>(obs: Observable<T>[]) => {
	return obs.length ? forkJoin(obs) : of([]);	
}

@Component({
	selector: 'acces-association-arrets-dialog',
	templateUrl: 'acces-association-arrets-dialog.html',
	styleUrls: ['acces-association-arrets-dialog.css'],
})
export class AccesAssociationArretsDialogComponent {
	// Gestion globale de la fenetre
	displaySubject = new Subject<boolean>();

	// Donnees a lier sur la vue
	loaded = false;
	
	accesDatas!: {
		acces: AccesTLR,
		ouvrages: OuvrageAccesDTO[],
	}[];
	currentIndex!: number; 
	
	// Saisies de l'utilisateur
	commentaireArrets!: string;
	arretsAcces!: ArretSlbTLR[];
	shouldDisableButtons: boolean = false;
	
	shouldSignalAbsenceArret: boolean = false;

	constructor(
		private dialog: MatDialog,

		@Inject(AccesService) private accesService: AccesService,
	) {
		// A la reception d'une demande d'affichage
		// Charger l'acces correspondant
		let acces$ = this.displayCommandsSubject.pipe(
			mergeMap(
				(accesIds) => {
					let results = accesIds.map(accesId => this.accesService.getById(accesId));
					return safeForkJoin(results);
				}
			)
		);

		// Une fois l'acces obtenu, charger toutes les donnees complementaires
		let accesDatas$ = acces$.pipe(
			mergeMap(
				(acces) => {
					let accesData = acces.map(oneAcces => {
						let ouvrages$ = accesService.getOuvrageDTOByAcces(oneAcces.id);

						return forkJoin({
							acces: of(oneAcces),
							ouvrages: ouvrages$,
						});
					});
					
					return safeForkJoin(accesData);
				}
			)
		);

		// Uniquement quand tous les elements sont charges, on bind les donnees sur les vue
		accesDatas$.subscribe(
			(accesDatas) => {
				this.accesDatas = accesDatas;
				this.currentIndex = 0;
				this.commentaireArrets = "";
				this.arretsAcces = [];

				this.loaded = true;
				this.displaySubject.next(true);
			}
		)

		// Quand on masque la fenetre de dialogue, on nettoie les donnees passees
		this.displaySubject.pipe(
			filter(show => !show)
		).subscribe(
			(_show) => {
				this.loaded = false;
			}
		)
	}

	@Input() id = 'accesAssociationArretsDialog';

	// Liaison de l'observable servant a commander l'affichage du panneau
	private displayCommandsSubject = new SubjectRelay<number[]>();
	@Input() set displayCommands(obs: Observable<number[]> | undefined) {
		this.displayCommandsSubject.registerSource(obs);
	}
	
	@Output() associationArretsSaved = new EventEmitter<AccesTLR>();
	
	save() {
		// NB: Pas de validation des saisies necessaires

		let command = new AssociationArretsOrder(
			this.accesDatas[this.currentIndex].acces.id,
			this.arretsAcces,
			this.commentaireArrets,
			this.shouldSignalAbsenceArret,
		);
		
		this.shouldDisableButtons = true;
		this.accesService.updateAssociationArrets(command).subscribe(
			(_id) => {
				this.shouldDisableButtons = false;
				this.associationArretsSaved.emit(this.accesDatas[this.currentIndex].acces);
				this.close();
			},
			(error: HttpErrorResponse) => {
				this.shouldDisableButtons = false;
				this.dialog.open(MessageDialogComponent, {
					width: '400px',
					data: {
						title: 'Enregistrement impossible',
						messages: ['Le serveur a rencontré une erreur imprévue']
					}
				});
				console.error("Erreur lors la tentivate d'enregistrement de l'acces #" + this.accesDatas[this.currentIndex].acces.id, error);
			}
		);
	}
	
	close() {
		this.currentIndex++;
		if (this.currentIndex < this.accesDatas.length) {
			this.commentaireArrets = "";
			this.arretsAcces = [];
		} else {
			this.displaySubject.next(false);
		}
	}
	
	signalerNoArretsFound(value: boolean){
		this.shouldSignalAbsenceArret = value;
	}
}